import { getContent } from '../../../shared/content.js';
import { Blocks, RichText } from '../../components/RichText.js';
import { useLocale } from '../../i18n.js';
import { BRAND } from '../../config/brand.js';

/**
 * Privacy.
 *
 * Two halves, deliberately separated: what the product does with the answers, and what
 * the surrounding infrastructure sees. Conflating them is how privacy pages end up
 * making claims the system cannot keep.
 *
 * Nothing here is an absolute. No "perfect anonymity", no "zero tracking", no "we can
 * never know". Every limitation that is real is written down.
 *
 * Brand and contact details are substituted from the central configuration rather than
 * written into each translation, so renaming the product or changing an address does not
 * mean editing four language files.
 */

/** Replace `{site}`, `{maker}`, `{privacy}` and `{support}` in translated copy. */
function fill(template: string): string {
  return template
    .replace(/\{site\}/g, BRAND.siteName)
    .replace(/\{maker\}/g, BRAND.legalEntity)
    .replace(/\{privacy\}/g, BRAND.contactPrivacy)
    .replace(/\{support\}/g, BRAND.contactSupport);
}

export function Privacy() {
  const c = getContent(useLocale()).privacy;

  return (
    <section className="section">
      <div className="section__inner section__inner--narrow prose">
        <h1 className="section__title">{c.heading}</h1>
        <p className="section__lede">{c.lede}</p>

        <Blocks blocks={c.blocks} />

        <table className="table">
          <thead>
            <tr>
              <th scope="col">{c.tableHeadWhere}</th>
              <th scope="col">{c.tableHeadWhat}</th>
            </tr>
          </thead>
          <tbody>
            {c.tableRows.map(([where, what]) => (
              <tr key={where}>
                <th scope="row">{where}</th>
                <td>
                  <RichText text={what} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Blocks blocks={c.afterTable} />

        <h2>{c.whoHeading}</h2>
        <p>
          <RichText text={fill(c.whoBody)} />
        </p>

        <h2>{c.contactHeading}</h2>
        <p>
          <RichText text={fill(c.contactBody)} />
        </p>
        <p>{c.contactNote}</p>
      </div>
    </section>
  );
}
